// Shared "how close is this card to a bingo" helpers.
//
// The line index sets below mirror WIN_CONDITIONS in the backend's Entities.js
// (5 rows, 5 columns, both diagonals). If the backend win conditions change,
// these have to change with them.

const SIZE = 5;
const CELL_COUNT = SIZE * SIZE;

const indices = (count, fn) => Array.from({ length: count }, (_, i) => fn(i));

const ROWS = indices(SIZE, (row) => indices(SIZE, (col) => row * SIZE + col));
const COLUMNS = indices(SIZE, (col) => indices(SIZE, (row) => row * SIZE + col));
const DIAGONAL = indices(SIZE, (i) => i * SIZE + i);
const ANTI_DIAGONAL = indices(SIZE, (i) => i * SIZE + (SIZE - 1 - i));

export const BINGO_LINES = [...ROWS, ...COLUMNS, DIAGONAL, ANTI_DIAGONAL];
export const BLACKOUT_LINE = indices(CELL_COUNT, (i) => i);

/**
 * Whether a game mode name means "fill the whole card to win".
 * The backend stores the mode as its display name, e.g. 'Blackout Bingo'.
 * @param {string} gameMode
 * @returns {boolean}
 */
export const isBlackoutMode = (gameMode) =>
  typeof gameMode === 'string' && gameMode.toLowerCase().includes('blackout');

/**
 * The set of lines that can win in the given game mode.
 * @param {string} gameMode
 * @returns {Array<Array<number>>}
 */
export const getWinningLines = (gameMode) =>
  isBlackoutMode(gameMode) ? [BLACKOUT_LINE] : BINGO_LINES;

/**
 * How many squares a card still needs for its closest possible win.
 *
 * @param {Array<number>} valueMap Flat value map, 1 for daubed
 * @param {string} [gameMode] Game mode name, used to pick the win pattern
 * @returns {{remaining: number, line: Array<number>, missing: Array<number>, hasBingo: boolean, lineLength: number}}
 *   remaining is the squares to go for the closest line, line is that line's
 *   cell indices (for highlighting), missing is just its undaubed cells and
 *   lineLength is how many squares a win needs (5 normally, 25 for blackout).
 */
export const getBingoProgress = (valueMap, gameMode) => {
  const lines = getWinningLines(gameMode);
  const lineLength = lines[0].length;
  const empty = { remaining: lineLength, line: [], missing: [], hasBingo: false, lineLength };

  if (!Array.isArray(valueMap) || valueMap.length !== CELL_COUNT) return empty;

  let best = null;
  for (const line of lines) {
    const missing = line.filter((index) => valueMap[index] !== 1);
    if (!best || missing.length < best.missing.length) best = { line, missing };
    if (best.missing.length === 0) break;
  }
  if (!best) return empty;

  return {
    remaining: best.missing.length,
    line: best.line,
    missing: best.missing,
    hasBingo: best.missing.length === 0,
    lineLength,
  };
};

/**
 * Best progress across every card a player holds.
 * @param {Array<Array<number>>} valueMaps A player's value maps, one per card
 * @param {string} [gameMode]
 * @returns {{remaining: number, line: Array<number>, missing: Array<number>, hasBingo: boolean}}
 */
export const getBestBingoProgress = (valueMaps, gameMode) => {
  if (!Array.isArray(valueMaps) || valueMaps.length === 0) {
    return getBingoProgress(null, gameMode);
  }

  return valueMaps
    .map((valueMap) => getBingoProgress(valueMap, gameMode))
    .reduce((best, current) => (current.remaining < best.remaining ? current : best));
};
